/**
 * @prop --color: Color of link
 * @prop --hover-color: Hover color of link
 * @prop --text-decoration-line: Text decoration of link
 * @prop --hover-text-decoration-line: Hover text decoration of link
 * @prop --icon-size: Size of icon next to link
 * @prop --min-height: Min height of link
 *
 * @prop --stzh-link-color: **Global**: Color of link
 * @prop --stzh-link-hover-color: **Global**: Hover color of link
 * @prop --stzh-link-text-decoration-line: **Global**: Text decoration of link
 * @prop --stzh-link-hover-text-decoration-line: **Global**: Text decoration of link
 * @prop --stzh-link-icon-size: **Global**: Size of icon next to link
 * @prop --stzh-link-text-decoration-thickness: **Global**: Text decoration thickness
 * @prop --stzh-link-text-decoration-skip-ink: **Global**: Whether to skip ink in text decoration
 * @prop --stzh-link-text-underline-offset: **Global**: Text underline offset
 */

@mixin link--size-default() {
  --font-size: var(--stzh-font-milli-font-size);
  --line-height: var(--stzh-font-milli-text-line-height);
  --letter-spacing: normal;

  @include mq($from: medium) {
    --font-size: var(--stzh-font-centi-font-size);
    --line-height: var(--stzh-font-centi-text-line-height);
    --letter-spacing: normal;
  }
}

@mixin link--size-small() {
  --font-size: var(--stzh-font-micro-font-size);
  --line-height: var(--stzh-font-micro-text-line-height);
  --letter-spacing: normal;
}

@mixin link--size-inherit() {
  --font-size: inherit;
  --line-height: inherit;
  --letter-spacing: inherit;
}

:host {
  @include link--size-default;
  --color: #{$linkColor};
  --text-decoration-line: #{$linkTextDecorationLine};
  --icon-wrapper-height: #{$linkIconWrapperHeight};
  --icon-size: #{$linkIconSize};
  --height: var(--stzh-link-height, 40px);

  --hover-color: #{$linkHoverColor};
  --hover-text-decoration-line: #{$linkHoverTextDecorationLine};

  @include mq($from: medium) {
    --height: var(--stzh-link-height, auto);
  }

  color: var(--color);
  display: inline-grid;
  width: auto;
  min-height: var(--height);
  pointer-events: var(--stzh-link-pointer-events, auto);

  &[height="auto"] {
    --height: var(--stzh-link-auto-height, auto);
  }

  &[fullwidth]:not([fullwidth="false"]) {
    width: 100%;
    display: grid;
  }

  &[variant="secondary"] {
    --color: #{$linkSecondaryColor};
    --hover-color: #{$linkSecondaryHoverColor};
  }

  &[variant="back"] {
    --text-decoration-line: none;
    --hover-color: var(--color);
  }

  &[active]:not([active="false"]) {
    --color: var(--hover-color);
  }

  &[disabled]:not([disabled="false"]) {
    --color: #{$colorGrey60};
    --hover-color: #{$colorGrey60};
  }
}

:host(:where([size="small"])) {
  @include link--size-small;
}

:host(:where([size="inherit"])) {
  @include link--size-inherit;
}

//@each $breakpoint, $size in $breakpoints {
//  @include mq($from: $breakpoint) {
//    :host(:where([size-#{$breakpoint}="default"])) {
//      @include link--size-default;
//    }
//
//    :host(:where([size-#{$breakpoint}="small"])) {
//      @include link--size-small;
//    }
//
//    :host(:where([size-#{$breakpoint}="inherit"])) {
//      @include link--size-inherit;
//    }
//  }
//}

.stzh-link {
  font-size: var(--font-size);
  line-height: var(--line-height);
  letter-spacing: var(--letter-spacing);
  font-family: inherit;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: stretch;
  appearance: none;
  border: none;
  color: var(--color);
  transition: color $baseTransitionAnimationSpeed;
  cursor: pointer;
  width: 100%;
  text-decoration-line: none;
  background-color: transparent;
  padding: 0;
  border-radius: $buttonBorderRadius;
  text-align: left;

  &:hover {
    color: var(--hover-color);

    @media (hover: none) {
      color: inherit;
    }
  }

  &:hover &__text {
    text-decoration-line: var(--hover-text-decoration-line);
  }

  &__vhidden {
    @include visuallyhidden;
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
  }

  &__icon-wrapper {
    --stzh-icon-size: var(--icon-size);

    display: none;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: auto;
    height: var(--icon-wrapper-height);
  }

  &__text {
    --stzh-base-color: initial;

    text-decoration-line: var(--text-decoration-line);
  }

  &__icon-wrapper:not(:empty) + &__text:not(:empty),
  &__text:not(:empty) + &__icon-wrapper:not(:empty) {
    margin-left: space("xxsmall");
  }

  &__badge  {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
  }

  &--has-icon &__icon-wrapper {
    display: inline-flex;
  }

  /* Variant if link only has icon */

  &--has-icon-only &__text {
    @include visuallyhidden;
  }

  /* Badge positioning variants */

  &--badge-position-link &__icon-wrapper {
    position: static;
  }

  &__badge,
  &--badge-position-link &__badge {
    top: calc(#{space("xxsmall")} * -1);
    right: calc(#{space("large")} * -1);
  }

  &--badge-position-icon#{&}--has-icon &__icon-wrapper {
    position: relative;
  }

  &--badge-position-icon#{&}--has-icon &__badge {
    top: calc(#{space("xsmall")} * -1);
    right: calc(#{space("small")} * -1);
  }

  &--badge-position-icon#{&}--has-icon#{&}--has-badge
    &__icon-wrapper
    + &__text:not(:empty) {
    margin-left: space("small");
  }

  /* Alignment variants */

  &--align-left &__inner {
    justify-content: flex-start;
  }

  &--align-right &__inner {
    justify-content: flex-end;
  }

  &--align-center &__inner {
    justify-content: center;
  }

  &--align-space-between &__inner {
    justify-content: space-between;
  }

  /* Disabled variant */

  &--is-disabled {
    cursor: not-allowed;
  }
}
